import { ref, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUiStore, useEmailAuthStore } from '@/stores'
import { useEmails } from '@/composables/useEmails'
import { useNotification } from '@/composables/useNotification'
import type { EmailAttachContext } from '@/composables/useEmailAsAttachment'
import type { ParsedEml } from '@/utils/emlPreview'
import { buildEmbeddedComposeInput, type EmbeddedAction } from '@/utils/embeddedEmailReply'

/**
 * Responder/Responder a todos/Reenviar desde el visor de un correo adjunto
 * (.eml). Primero busca el hilo real por Message-ID en Firestore; si no
 * existe, abre un correo nuevo armado con los datos del embebido.
 */
export function useEmbeddedEmailReply(): {
  busyAction: Ref<EmbeddedAction | null>
  respondToEmbedded(eml: ParsedEml, action: EmbeddedAction, context?: EmailAttachContext): Promise<boolean>
} {
  const { t, locale } = useI18n()
  const uiStore = useUiStore()
  const emailAuthStore = useEmailAuthStore()
  const { findEmailIdByInternetMessageId } = useEmails()
  const { showNotification } = useNotification()

  const busyAction = ref<EmbeddedAction | null>(null)

  async function respondToEmbedded(
    eml: ParsedEml,
    action: EmbeddedAction,
    context?: EmailAttachContext
  ): Promise<boolean> {
    if (busyAction.value) return false
    busyAction.value = action

    try {
      if (!emailAuthStore.isAuthenticated) {
        showNotification('warning', t('emailComposer.embedded.errorTitle'), t('emailComposer.embedded.notConnected'))
        return false
      }

      // Hilo real: si el embebido también llegó al buzón de alguien de la
      // compañía, la respuesta/reenvío se abre sobre ESE documento (adjuntos
      // del proveedor, y sale por /:id/reply o /:id/forward).
      // El Message-ID lo controla un tercero: solo se usa el documento si
      // además coincide el remitente, para que un Message-ID adivinado o
      // reciclado no abra el hilo de un correo interno ajeno al que se ve en
      // el visor.
      if (eml.messageId) {
        let found: { id: string; fromAddress: string } | null = null
        try {
          found = await findEmailIdByInternetMessageId(eml.messageId)
        } catch (err) {
          console.warn('[EmbeddedReply] búsqueda por Message-ID:', err)
        }
        if (found && found.fromAddress.trim().toLowerCase() === eml.fromAddress.trim().toLowerCase()) {
          return uiStore.openComposer(action, found.id, context?.module, context?.entityId, context?.entityIdNumber) !== null
        }
      }

      // Correo nuevo, armado con los datos del embebido.
      const input = buildEmbeddedComposeInput(eml, action, {
        ownAddress: emailAuthStore.userEmail || '',
        locale: locale.value,
        labels: {
          from: t('emailComposer.quoted.from'),
          sent: t('emailComposer.quoted.sent'),
          to: t('emailComposer.quoted.to'),
          cc: t('emailComposer.quoted.cc'),
          subject: t('emailComposer.quoted.subject'),
        },
      })

      return uiStore.openComposer('new', undefined, context?.module, context?.entityId, context?.entityIdNumber, null, {
        initialTo: input.to,
        initialCc: input.cc,
        initialSubject: input.subject,
        initialBodyHtml: input.bodyHtml,
        attachments: input.attachments,
        threadHeaders: input.inReplyTo ? { inReplyTo: input.inReplyTo, references: input.references } : undefined,
        dedupeKey: eml.messageId ? `embedded:${action === 'forward' ? 'forward' : 'reply'}:${eml.messageId}` : undefined,
      }) !== null
    } catch (err) {
      console.error('[EmbeddedReply]', err)
      showNotification('error', t('emailComposer.embedded.errorTitle'), t('emailComposer.asAttachment.unexpected'))
      return false
    } finally {
      busyAction.value = null
    }
  }

  return { busyAction, respondToEmbedded }
}
